"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDuration = getDuration;
exports.formatDuration = formatDuration;
exports.durationHasLength = durationHasLength;
exports.sumDurations = sumDurations;
exports.simplifyDuration = simplifyDuration;
const unique_1 = __importDefault(require("./unique"));
const UNITS_TO_DAYS = {
    days: 1,
    weeks: 7,
    months: 30,
    years: 365,
};
function getDuration(milliseconds) {
    const seconds = milliseconds / 1000;
    return {
        hours: Math.floor(seconds / (60 * 60)),
        minutes: Math.floor((seconds % (60 * 60)) / 60),
        seconds: Math.floor(seconds % 60),
    };
}
function formatDuration({ units, countdown, duration: { hours = 0, minutes = 0, seconds = 0 }, }) {
    let formattedDuration = countdown ? "-" : "";
    // Display the time with units (e.g. "1h 10m")
    if (units) {
        formattedDuration += [
            hours && `${hours}h`,
            minutes && `${minutes}min`,
            !hours && (!minutes || seconds) && `${seconds}s`,
        ]
            .filter(Boolean)
            .join(" ");
    }
    // Display the time without units (e.g. "1:10:00")
    else {
        formattedDuration += [
            hours ? hours.toString() : "",
            hours ? padDuration(minutes) : minutes.toString(),
            padDuration(seconds),
        ]
            .filter(Boolean)
            .join(":");
    }
    return formattedDuration;
}
function padDuration(duration) {
    const str = duration.toString();
    return str.length < 2 ? "0" + str : str;
}
/**
 * Returns `true` for durations that have a non-zero value for at least one unit
 */
function durationHasLength(duration) {
    return Boolean(duration && Object.values(duration).filter(Boolean).length > 0);
}
/**
 * Sum two or more durations by adding the values of corresponding keys. The
 * result is not normalized in any way, so you might get an object like
 * `{ days: 15, weeks: 5, months: 1 }`.
 */
function sumDurations(...durations) {
    return durations.reduce((sum, current) => {
        const units = (0, unique_1.default)([...Object.keys(sum), ...Object.keys(current)]);
        return Object.fromEntries(units.map((unit) => [
            unit,
            (sum[unit] || 0) +
                (current[unit] || 0),
        ]));
    });
}
/**
 * This converts a duration object to a format that can be easily i18n-ized:
 * - Units less than 1 day are stripped out.
 * - If the duration has no length a duration of 0 days is returned.
 * - Extra units with a value of zero are stripped out.
 * - If the duration has multiple non-zero units than it is converted to a
 *   duration composed only of days.
 */
function simplifyDuration(duration) {
    // Strip out units less than one day
    const rounded = Object.fromEntries(Object.entries(duration).filter(([unit]) => unit === "days" ||
        unit === "weeks" ||
        unit === "months" ||
        unit === "years"));
    let count;
    let unit;
    if (!Object.values(rounded).filter(Boolean).length) {
        // The duration has no length, so just go with 0 days
        unit = "days";
        count = 0;
    }
    else if (Object.values(rounded).filter(Boolean).length > 1) {
        // The duration is composed of multiple units, so convert it to days
        unit = "days";
        count = Object.entries(rounded).reduce((result, [unit, value]) => {
            if (unit in UNITS_TO_DAYS) {
                return (result + UNITS_TO_DAYS[unit] * value);
            }
            return result;
        }, 0);
    }
    else {
        // eslint-disable-next-line @typescript-eslint/no-extra-semi
        ;
        [unit, count] = Object.entries(rounded).find(([, value]) => value);
    }
    return { [unit]: count };
}
